import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import FuturaIcon from "../components/FuturaIcon";
import { useAuth, API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import axios from "axios";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    try {
      const savedEmail = localStorage.getItem("futura_remember_email");
      if (savedEmail) {
        setEmail(savedEmail);
        setRememberMe(true);
      }
    } catch (e) { console.error("Remember-me read failed:", e); }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });

      // Check if email needs verification
      if (response.data.email_verified === false) {
        navigate("/verify-email", { state: { email } });
        return;
      }

      try {
        if (rememberMe) {
          localStorage.setItem("futura_remember_email", email);
        } else {
          localStorage.removeItem("futura_remember_email");
        }
        localStorage.removeItem("futura_remember");
      } catch (e) { console.error("Remember-me save failed:", e); }
      login(response.data);
      // Prefetch critical endpoints in parallel (fire-and-forget) so the dashboard
      // renders with hot caches — eliminates the "empty shell" flash on first navigation.
      Promise.all([
        axios.get(`${API}/avatar/state`).catch(() => {}),
        axios.get(`${API}/avatar/challenge`).catch(() => {}),
        axios.get(`${API}/mood/history`).catch(() => {}),
      ]);
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(" "));
      else setError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-4" data-testid="login-page">
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a0a0f] via-[#0d1020] to-[#0a0a0f] pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#002FA7] to-[#5B21B6] flex items-center justify-center p-1.5">
              <FuturaIcon className="w-full h-full text-white" />
            </div>
            <span className="font-['Outfit'] font-semibold text-2xl">FUTURA</span>
          </Link>
          <h1 className="font-['Outfit'] text-2xl font-semibold mb-2" data-testid="login-title">{t("login_title")}</h1>
          <p className="text-white/50 text-sm">{t("login_subtitle")}</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label className="text-white/70 text-sm mb-2 block">{t("login_email")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" required autoComplete="email" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-white/70 text-sm">{t("login_password")}</Label>
                <Link to="/forgot-password" className="text-xs text-[#002FA7] hover:underline" data-testid="forgot-password-link">{t("login_forgot_password")}</Link>
              </div>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="********" required autoComplete="current-password" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl pr-12" data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="remember-me-toggle" onClick={() => setRememberMe(!rememberMe)}>
              <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${rememberMe ? "bg-[#002FA7] border-[#002FA7]" : "border-white/20 bg-white/5"}`}>
                {rememberMe && <svg className="w-3 h-3 text-white" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" fill="none" /></svg>}
              </div>
              <span className="text-sm text-white/60">{t("login_remember_me")}</span>
            </label>

            {error && <p className="text-red-400 text-sm text-center" data-testid="login-error">{error}</p>}

            <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-gradient-to-r from-[#002FA7] to-[#5B21B6] hover:opacity-90 font-medium" data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("login_submit")}
            </Button>
          </form>
        </div>

        <p className="text-center mt-6 text-sm text-white/40">
          {t("login_no_account")} <Link to="/register" className="text-[#002FA7] hover:underline font-medium" data-testid="register-link">{t("login_register_link")}</Link>
        </p>

        <div className="flex justify-center mt-4">
          <LanguageSelector variant="dark" />
        </div>
      </div>
    </div>
  );
};

export default Login;
